import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AwardCommitteeMeeting, Page, PageMetadata } from '@tran-monorepo/interfaces';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
    CreateAwardCommitteeMeetingRequest,
    CreateDeadlineRequest,
    Deadline,
    UpdateAwardCommitteeMeetingRequest,
    UpdateDeadlineRequest
} from '../interfaces/award-committee-meeting.interface';

@Injectable({
    providedIn: 'root'
})
export class AwardCommitteeService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiConfig[1].uri}/v1/award-committee-meetings`;

    // ==================== Award Committee Meetings ====================

    /**
     * Get a specific Award Committee Meeting by ID
     */
    public getMeetingById(meetingId: string): Observable<AwardCommitteeMeeting> {
        return this.http.get<AwardCommitteeMeeting>(`${this.baseUrl}/${meetingId}`);
    }

    /**
     * Get paginated list of Award Committee Meetings
     */
    public getMeetings(
        page = 0,
        pageSize = 10,
        orderBy?: string,
        sortOrder?: 'asc' | 'desc'
    ): Observable<Page<AwardCommitteeMeeting, PageMetadata>> {
        let params = new HttpParams().set('page', page.toString()).set('pageSize', pageSize.toString());
        if (orderBy) {
            params = params.set('orderBy', orderBy);
        }
        if (sortOrder) {
            params = params.set('sortOrder', sortOrder.toUpperCase());
        }
        return this.http.get<Page<AwardCommitteeMeeting, PageMetadata>>(this.baseUrl, { params });
    }

    /**
     * Create a new Award Committee Meeting
     */
    public createMeeting(request: CreateAwardCommitteeMeetingRequest): Observable<AwardCommitteeMeeting> {
        return this.http.post<AwardCommitteeMeeting>(this.baseUrl, request);
    }

    /**
     * Update an existing Award Committee Meeting
     */
    public updateMeeting(id: string, request: UpdateAwardCommitteeMeetingRequest): Observable<AwardCommitteeMeeting> {
        return this.http.patch<AwardCommitteeMeeting>(`${this.baseUrl}/${id}`, request);
    }

    /**
     * Delete an Award Committee Meeting
     */
    public deleteMeeting(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}`);
    }

    // ==================== Deadlines ====================

    /**
     * Create a new Deadline within an Award Committee Meeting
     */
    public createDeadline(meetingId: string, request: CreateDeadlineRequest): Observable<Deadline> {
        return this.http.post<Deadline>(`${this.baseUrl}/${meetingId}/deadlines`, request);
    }

    /**
     * Update an existing Deadline
     */
    public updateDeadline(meetingId: string, deadlineId: string, request: UpdateDeadlineRequest): Observable<Deadline> {
        return this.http.patch<Deadline>(`${this.baseUrl}/${meetingId}/deadlines/${deadlineId}`, request);
    }

    /**
     * Delete a Deadline
     */
    public deleteDeadline(meetingId: string, deadlineId: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${meetingId}/deadlines/${deadlineId}`);
    }
}
